import { useState } from 'react';
import { KeyRound, Globe } from 'lucide-react';

import { useCredentials } from '../hooks/use-credentials';
import { useSystemCredentials } from '../hooks/use-system-credentials';
import { useAuth } from '../providers/auth-provider';
import { getCredentialType } from '../lib/credential-types';
import { AccessLogView } from './AccessLogView';
import { Button } from './Button';
import { CredentialEditor } from './CredentialEditor';
import { CredentialListItem } from './CredentialListItem';
import { CredentialTypePicker } from './CredentialTypePicker';
import { Input } from './Input';
import { PlansView } from './PlansView';

import type { CredentialTypeDefinition } from '../lib/credential-types';
import type { CredentialData, CredentialIsolation, CredentialStatus, SortOption } from '../lib/types';

const STATUS_CHIPS: { status: CredentialStatus; label: string }[] = [
  { status: 'active', label: 'Active' },
  { status: 'pending', label: 'Pending' },
  { status: 'expired', label: 'Expired' },
  { status: 'revoked', label: 'Revoked' },
];

const ISOLATION_CHIPS: { value: CredentialIsolation; label: string }[] = [
  { value: 'personal', label: 'Personal' },
  { value: 'tenant', label: 'Team' },
];

const ISOLATION_CHIP_CLASSES = {
  active: 'bg-sd-info/20 text-sd-info border-sd-info/30 hover:bg-sd-info/25',
  inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
};

const STATUS_CHIP_CLASSES: Record<CredentialStatus, { active: string; inactive: string }> = {
  active: {
    active: 'bg-sd-success/20 text-sd-success border-sd-success/30 hover:bg-sd-success/25',
    inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
  },
  pending: {
    active: 'bg-sd-warning/20 text-sd-warning border-sd-warning/30 hover:bg-sd-warning/25',
    inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
  },
  expired: {
    active: 'bg-sd-error/20 text-sd-error border-sd-error/30 hover:bg-sd-error/25',
    inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
  },
  revoked: {
    active: 'bg-sd-error/20 text-sd-error border-sd-error/30 hover:bg-sd-error/25',
    inactive: 'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50',
  },
};

function CredentialsViewSkeleton() {
  return (
    <div data-testid="credentials-skeleton" className="flex flex-col h-full bg-sd-background animate-pulse">
      <div className="flex items-center justify-between px-sd-3 pt-sd-3 pb-sd-1">
        <div className="h-6 w-28 bg-sd-border/30 rounded-sd-md" />
        <div className="h-8 w-32 bg-sd-border/30 rounded-sd-md" />
      </div>
      <div className="p-sd-2">
        <div className="h-10 bg-sd-border/20 rounded-sd-md" />
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-sd-2 px-sd-3 py-sd-3 border-b border-sd-border/20">
          <div className="w-8 h-8 rounded-sd-full bg-sd-border/30" />
          <div className="flex-1">
            <div className="h-4 w-32 bg-sd-border/30 rounded mb-1" />
            <div className="h-3 w-20 bg-sd-border/20 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CredentialsView() {
  const {
    credentials,
    status,
    isRefreshing,
    error,
    counts,
    isolationCounts,
    filters,
    setFilters,
    reload,
    createCredential,
    updateCredential,
    deleteCredential,
    revokeCredential,
  } = useCredentials({ enabled: true });

  const { user } = useAuth();
  const isRootTenant = user?.tenant_id === 'tnt_root';
  const isOwner = user?.role === 'owner';

  const {
    credentials: systemCredentials,
    status: systemStatus,
    enableCredential,
    disableCredential,
  } = useSystemCredentials({ enabled: true });

  const [activeTab, setActiveTab] = useState<'credentials' | 'system' | 'access-log' | 'plans'>('credentials');
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [editorState, setEditorState] = useState<{ isOpen: boolean; mode: 'create' | 'edit'; typeDef: CredentialTypeDefinition | null; credential: CredentialData | null }>({
    isOpen: false, mode: 'create', typeDef: null, credential: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleTypeSelected = (typeDef: CredentialTypeDefinition) => {
    setShowTypePicker(false);
    setEditorState({ isOpen: true, mode: 'create', typeDef, credential: null });
  };

  const handleEdit = (cred: CredentialData) => {
    const typeDef = cred.credential_type ? getCredentialType(cred.credential_type) : undefined;
    if (!typeDef) return;
    setEditorState({
      isOpen: true,
      mode: 'edit',
      typeDef,
      credential: cred,
    });
  };

  const handleSave = async (data: Parameters<typeof createCredential>[0]) => {
    setIsSaving(true);
    try {
      if (editorState.mode === 'create') {
        await createCredential(data);
      } else if (editorState.credential) {
        await updateCredential(editorState.credential.id, {
          name: data.name,
          sharing_mode: data.sharing_mode,
          isolation: data.isolation,
          fields: data.fields,
        });
      }
      setEditorState({ isOpen: false, mode: 'create', typeDef: null, credential: null });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await revokeCredential(id, 'Revoked by user');
    } catch { /* handled by hook */ }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteCredential(id);
    } catch { /* handled by hook */ }
  };

  if (status === 'loading') {
    return <CredentialsViewSkeleton />;
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center h-full p-sd-4 text-center">
        <p className="text-sd-error mb-sd-3">{error ?? 'Failed to load credentials'}</p>
        <Button variant="primary" onClick={reload}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-full bg-sd-background">
      {/* Refresh indicator */}
      {isRefreshing && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-sd-warning/60 animate-pulse z-10" />
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-sd-3 pt-sd-3 pb-sd-1">
        <h2 className="text-sd-lg font-semibold text-sd-text">Credentials</h2>
        {activeTab === 'credentials' && (
          <Button variant="primary" size="sm" onClick={() => setShowTypePicker(true)}>+ New Credential</Button>
        )}
      </div>

      {/* Sub-nav tabs */}
      <div className="flex gap-sd-1 px-sd-3 pb-sd-1" role="tablist" aria-label="Credentials navigation">
        <button
          role="tab"
          aria-selected={activeTab === 'credentials'}
          className={`px-sd-3 py-sd-1 text-sd-sm font-medium rounded-sd-full border transition-colors duration-150 ${activeTab === 'credentials' ? 'bg-sd-surface-active text-sd-text border-sd-border/30' : 'bg-transparent text-sd-text-secondary border-transparent hover:bg-sd-surface-elevated/60'}`}
          onClick={() => setActiveTab('credentials')}
        >
          Credentials
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'system'}
          className={`px-sd-3 py-sd-1 text-sd-sm font-medium rounded-sd-full border transition-colors duration-150 ${activeTab === 'system' ? 'bg-sd-surface-active text-sd-text border-sd-border/30' : 'bg-transparent text-sd-text-secondary border-transparent hover:bg-sd-surface-elevated/60'}`}
          onClick={() => setActiveTab('system')}
        >
          System
          {systemCredentials.length > 0 && (
            <span className="ml-sd-1 text-sd-xs opacity-70">{systemCredentials.length}</span>
          )}
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'access-log'}
          className={`px-sd-3 py-sd-1 text-sd-sm font-medium rounded-sd-full border transition-colors duration-150 ${activeTab === 'access-log' ? 'bg-sd-surface-active text-sd-text border-sd-border/30' : 'bg-transparent text-sd-text-secondary border-transparent hover:bg-sd-surface-elevated/60'}`}
          onClick={() => setActiveTab('access-log')}
        >
          Access Log
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'plans'}
          className={`px-sd-3 py-sd-1 text-sd-sm font-medium rounded-sd-full border transition-colors duration-150 ${activeTab === 'plans' ? 'bg-sd-surface-active text-sd-text border-sd-border/30' : 'bg-transparent text-sd-text-secondary border-transparent hover:bg-sd-surface-elevated/60'}`}
          onClick={() => setActiveTab('plans')}
        >
          Plans
        </button>
      </div>

      {activeTab === 'plans' ? (
        <PlansView enabled />
      ) : activeTab === 'system' ? (
        <div className="flex flex-col flex-1 overflow-hidden">
          <div className="px-sd-3 py-sd-2 bg-sd-surface-elevated border-b border-sd-border/20">
            <div className="flex items-center gap-sd-2 text-sd-sm text-sd-text-secondary">
              <Globe size={14} className="text-sd-info" />
              {isRootTenant
                ? 'Platform-wide credentials managed by the root tenant. All tenants can opt-in.'
                : isOwner
                  ? 'Enable system credentials to make them available across your team.'
                  : 'System credentials enabled by your team owner.'}
            </div>
          </div>
          {systemCredentials.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 p-sd-4 text-center">
              <div className="w-12 h-12 rounded-sd-lg bg-sd-info/10 flex items-center justify-center mb-sd-3">
                <Globe size={24} className="text-sd-info/60" />
              </div>
              <p className="text-sd-lg font-semibold text-sd-text mb-sd-1">No system credentials</p>
              <p className="text-sd-sm text-sd-text-muted max-w-xs">
                {isRootTenant
                  ? 'Create platform-wide credentials that tenants can opt into.'
                  : 'No system credentials are available yet.'}
              </p>
            </div>
          ) : (
            <ul role="list" aria-label="System credentials" className="flex-1 overflow-y-auto">
              {systemCredentials.map((cred) => (
                <CredentialListItem
                  key={cred.id}
                  id={cred.id}
                  name={cred.name}
                  serviceName={cred.service_name}
                  credentialType={cred.credential_type}
                  status={cred.status}
                  isolation={cred.isolation}
                  createdAt={cred.created_at}
                  isEnabledForTenant={cred.is_enabled_for_tenant}
                  showEnableToggle={isOwner && !isRootTenant}
                  onToggleEnable={() => {
                    if (cred.is_enabled_for_tenant) {
                      void disableCredential(cred.id);
                    } else {
                      void enableCredential(cred.id);
                    }
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      ) : activeTab === 'access-log' ? (
        <AccessLogView enabled />
      ) : (
      <>
      {/* Filter bar */}
      <div className="flex flex-col gap-sd-2 p-sd-2 bg-sd-surface-elevated @md:flex-row @md:items-center shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <div className="flex flex-wrap gap-sd-1">
          {STATUS_CHIPS.map(({ status: s, label }) => {
            const isActive = filters.statuses.includes(s);
            return (
              <span
                key={s}
                role="button"
                tabIndex={0}
                aria-label={`Filter by ${label}`}
                aria-pressed={isActive}
                className={`inline-flex items-center px-sd-2 py-sd-1 text-sd-sm font-medium border rounded-sd-full cursor-pointer select-none transition-all duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${isActive ? STATUS_CHIP_CLASSES[s].active : STATUS_CHIP_CLASSES[s].inactive}`}
                onClick={() => {
                  const statuses = filters.statuses.includes(s) ? filters.statuses.filter((x) => x !== s) : [...filters.statuses, s];
                  setFilters({ ...filters, statuses });
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const statuses = filters.statuses.includes(s) ? filters.statuses.filter((x) => x !== s) : [...filters.statuses, s];
                    setFilters({ ...filters, statuses });
                  }
                }}
              >
                {label}
                {counts[s] > 0 && (
                  <span className="ml-sd-1 text-sd-xs opacity-70">{counts[s]}</span>
                )}
              </span>
            );
          })}
          <span className="mx-sd-1 w-px h-4 bg-sd-border/30 self-center" />
          {ISOLATION_CHIPS.map(({ value, label }) => {
            const isActive = filters.isolation === value;
            return (
              <span
                key={value}
                role="button"
                tabIndex={0}
                aria-label={`Filter by ${label}`}
                aria-pressed={isActive}
                className={`inline-flex items-center px-sd-2 py-sd-1 text-sd-sm font-medium border rounded-sd-full cursor-pointer select-none transition-all duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${isActive ? ISOLATION_CHIP_CLASSES.active : ISOLATION_CHIP_CLASSES.inactive}`}
                onClick={() => setFilters({ ...filters, isolation: isActive ? null : value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFilters({ ...filters, isolation: isActive ? null : value });
                  }
                }}
              >
                {label}
                {isolationCounts[value] > 0 && (
                  <span className="ml-sd-1 text-sd-xs opacity-70">{isolationCounts[value]}</span>
                )}
              </span>
            );
          })}
        </div>
        <Input
          id="credential-search"
          variant="search"
          placeholder="Search credentials..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <select
          aria-label="Sort credentials by"
          value={filters.sortBy}
          onChange={(e) => setFilters({ ...filters, sortBy: e.target.value as SortOption })}
          className="bg-sd-surface-elevated border border-sd-border/30 rounded-sd-sm px-sd-2 py-sd-1 text-sd-sm text-sd-text focus:ring-2 focus:ring-sd-primary focus:outline-none transition-colors duration-150 motion-reduce:transition-none"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="recently-updated">Recently Updated</option>
        </select>
      </div>

      {/* Content */}
      {credentials.length === 0 ? (
        <div data-testid="credentials-empty" className="flex flex-col items-center justify-center flex-1 p-sd-4 text-center">
          <div className="w-12 h-12 rounded-sd-lg bg-sd-warning/10 flex items-center justify-center mb-sd-3 sd-reveal-1">
            <KeyRound size={24} className="text-sd-warning/60" />
          </div>
          <p className="text-sd-lg font-semibold text-sd-text mb-sd-1 sd-reveal-2">No credentials yet</p>
          <p className="text-sd-sm text-sd-text-muted max-w-xs sd-reveal-3">
            Add credentials so agents can securely access services on your behalf.
          </p>
        </div>
      ) : (
        <ul role="list" aria-label="Credentials" className="flex-1 overflow-y-auto">
          {credentials.map((cred) => (
            <CredentialListItem
              key={cred.id}
              id={cred.id}
              name={cred.name}
              serviceName={cred.service_name}
              credentialType={cred.credential_type}
              status={cred.status}
              isolation={cred.isolation}
              createdAt={cred.created_at}
              onClick={() => handleEdit(cred)}
              onEdit={() => handleEdit(cred)}
              onRevoke={cred.status === 'active' ? () => { void handleRevoke(cred.id); } : undefined}
              onDelete={!cred.plan_id ? () => { void handleDelete(cred.id); } : undefined}
            />
          ))}
        </ul>
      )}

      {/* Type Picker Modal */}
      <CredentialTypePicker
        isOpen={showTypePicker}
        onClose={() => setShowTypePicker(false)}
        onSelect={handleTypeSelected}
      />

      {/* Editor Modal */}
      {editorState.isOpen && editorState.typeDef && (
        <CredentialEditor
          isOpen={editorState.isOpen}
          onClose={() => setEditorState({ isOpen: false, mode: 'create', typeDef: null, credential: null })}
          onSave={(data) => { void handleSave(data); }}
          typeDef={editorState.typeDef}
          mode={editorState.mode}
          isSaving={isSaving}
          initialValues={editorState.credential ? {
            name: editorState.credential.name ?? undefined,
            sharing_mode: editorState.credential.sharing_mode,
            isolation: editorState.credential.isolation,
            fields: (editorState.credential.metadata?.fields as Record<string, unknown>) ?? {},
            access_level: editorState.credential.access_level,
          } : undefined}
        />
      )}
      </>
      )}
    </div>
  );
}
